import React, { FC } from 'react';
import { useI18nPath } from '../../../../config/clientRoutes/hooks';
import { useParams } from 'react-router';
import { ProgressAndScore } from './types';
import { Grid, Typography } from '@material-ui/core';
import { BlankLink, Tooltip } from '../../../shared/components';
import {
  SeriesCardPaper,
  SeriesCardScoresAndProgressInfo,
} from '../TrajectoryDetail/SeriesCard/index';
import { ExerciseSeries } from '../../../../config/api/models/Exercise';
import {
  GroupStudentSeriesScores,
  SeriesScores,
  StudentSeriesScores,
} from '../../../../config/api/models/Score';
import TeacherSeriesTooltip from './TeacherSeriesTooltip';
import { UrlParams } from '../../../shared/types';
import TotalExercisesByLevel from './TotalExercisesByLevel';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

type Props = {
  series: ExerciseSeries;
  teacherProgressAndScores?: GroupStudentSeriesScores;
};

const getProgressAndScoreBySeries = (
  seriesId: string,
  data: GroupStudentSeriesScores,
  selectedGroup: string
): ProgressAndScore | undefined => {
  if (selectedGroup === '') return undefined;

  let totalScore: number = 0;
  let totalMaxScore: number = 0;
  let completed: number = 0;
  let numStudents: number = Object.entries(data.students).length;
  let numBlocks: number = 0;

  Object.entries(data.students).forEach((student) => {
    const studentScoresForSeries: SeriesScores = student[1].scores[seriesId];
    if (!!studentScoresForSeries) {
      numBlocks = Object.entries(studentScoresForSeries).length;
      Object.entries(studentScoresForSeries).forEach((block) => {
        totalScore += block[1].isAnswered ? block[1].score : 0;
        totalMaxScore += block[1].isAnswered ? block[1].maxScore : 0;
        completed += block[1].isAnswered ? 1 : 0;
      });
    }
  });

  const score = totalMaxScore > 0 ? (totalScore / totalMaxScore) * 100 : 0;
  const scorePercentage = score < 1 ? Math.ceil(score) : Math.floor(score);

  const progress =
    completed > 0 && numStudents > 0 && numBlocks > 0
      ? (completed / numStudents / numBlocks) * 100
      : 0;
  const averageProgress = progress < 1 ? Math.ceil(progress) : Math.floor(progress);

  return {
    progress: `${averageProgress}%`,
    score: `${scorePercentage}%`,
  };
};

const TeacherSeriesItem: FC<Props> = ({ series, teacherProgressAndScores }) => {
  const { getPath } = useI18nPath();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const { bookId, chapterId, trajectoryId, sectionCode, sectionName } = useParams<UrlParams>();
  const progressAndScore: ProgressAndScore | undefined = !!teacherProgressAndScores
    ? getProgressAndScoreBySeries(series.id, teacherProgressAndScores, selectedGroup || '')
    : undefined;

  const tooltipInfo: Record<string, StudentSeriesScores> | undefined = teacherProgressAndScores
    ? teacherProgressAndScores.students
    : undefined;

  const Item = React.forwardRef((props, ref) => (
    <Grid item xs={12} sm={6} md={4} {...props} innerRef={ref}>
      <BlankLink
        to={getPath('trajectoryView', {
          bookId,
          chapterId,
          trajectoryId,
          'sectionCode?': sectionCode,
          'sectionName?': sectionName,
          'seriesId?': series.id,
        })}
      >
        <SeriesCardPaper
          className={progressAndScore ? 'show-progress-and-scores' : ''}
          elevation={0}
        >
          <Typography variant="h3">{series.title}</Typography>
          {!!progressAndScore && (
            <SeriesCardScoresAndProgressInfo progressAndScore={progressAndScore} />
          )}
          {!!series.blocksOfLevel && <TotalExercisesByLevel series={series} />}
        </SeriesCardPaper>
      </BlankLink>
    </Grid>
  ));

  return !!tooltipInfo ? (
    <Tooltip
      title={<TeacherSeriesTooltip data={tooltipInfo} seriesId={series.id} />}
      placement={'bottom'}
    >
      <Item />
    </Tooltip>
  ) : (
    <Item />
  );
};

export default TeacherSeriesItem;
